import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Printer, Zap, Package } from 'lucide-react';
import { generateInternalBarcode, addAssetToList } from '@/data/mockAssets';
import { createMockAsset } from '@/data/mockData';
import { toast } from 'sonner';
import type { IntakeLine, Asset } from '@/types/models';

interface ConvertLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: IntakeLine | null;
  siteId: string;
  workOrderId: string;
  operator?: string;
  onConverted: (line: IntakeLine, assets: Asset[]) => void;
}

export default function ConvertLineDialog({
  open, onOpenChange, line, siteId, workOrderId, operator, onConverted,
}: ConvertLineDialogProps) {
  const [bulkCount, setBulkCount] = useState(0);
  const [createdAssets, setCreatedAssets] = useState<Asset[]>([]);

  if (!line) return null;

  const remaining = line.quantity_remaining - createdAssets.length;
  const pct = line.quantity_total > 0
    ? Math.round(((line.quantity_serialized + createdAssets.length) / line.quantity_total) * 100)
    : 0;

  function createOne() {
    if (remaining <= 0) return;
    const barcode = generateInternalBarcode();
    const asset = createMockAsset({
      work_order_id: workOrderId,
      intake_line_id: line!.id,
      serial_number: undefined,
      barcode,
      category_id: line!.category_id,
      category_name: line!.category_name,
      condition: 'good',
      status: 'received',
      tracking_mode: 'serialized',
      quantity: 1,
      site_id: siteId,
      scanned_by: operator,
    });
    addAssetToList(asset);
    setCreatedAssets((prev) => [asset, ...prev]);
    toast.success(`Created ${asset.internal_barcode}`);
  }

  function createBulk() {
    if (bulkCount <= 0 || bulkCount > remaining) return;
    const assets: Asset[] = [];
    for (let i = 0; i < bulkCount; i++) {
      const barcode = generateInternalBarcode();
      const asset = createMockAsset({
        work_order_id: workOrderId,
        intake_line_id: line!.id,
        serial_number: undefined,
        barcode,
        category_id: line!.category_id,
        category_name: line!.category_name,
        condition: 'good',
        status: 'received',
        tracking_mode: 'serialized',
        quantity: 1,
        site_id: siteId,
        scanned_by: operator,
      });
      addAssetToList(asset);
      assets.push(asset);
    }
    setCreatedAssets((prev) => [...assets.reverse(), ...prev]);
    toast.success(`Created ${assets.length} assets with generated serials`);
    setBulkCount(0);
  }

  function handleDone() {
    if (createdAssets.length > 0) {
      const updated: IntakeLine = {
        ...line!,
        quantity_serialized: line!.quantity_serialized + createdAssets.length,
        quantity_remaining: line!.quantity_remaining - createdAssets.length,
      };
      onConverted(updated, createdAssets);
    }
    setCreatedAssets([]);
    setBulkCount(0);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleDone}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Convert: {line.category_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Progress */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Serialization Progress</span>
              <span className="font-mono font-medium text-foreground">
                {line.quantity_serialized + createdAssets.length}/{line.quantity_total}
              </span>
            </div>
            <Progress value={pct} className="h-2" />
            <p className="text-xs text-muted-foreground">{remaining} items remaining</p>
          </div>

          {remaining > 0 && (
            <>
              {/* Single conversion */}
              <div className="flex items-center gap-3">
                <Button onClick={createOne} variant="outline" className="gap-2 flex-1">
                  <Zap className="h-4 w-4" /> Create Single Item
                </Button>
              </div>

              {/* Bulk conversion */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Bulk Convert</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={remaining}
                    value={bulkCount || ''}
                    onChange={(e) => setBulkCount(parseInt(e.target.value) || 0)}
                    placeholder={`Up to ${remaining}`}
                    className="flex-1"
                  />
                  <Button onClick={createBulk} disabled={bulkCount <= 0 || bulkCount > remaining} className="gap-2">
                    <Zap className="h-4 w-4" /> Generate {bulkCount || 0}
                  </Button>
                </div>
              </div>
            </>
          )}

          {remaining <= 0 && (
            <div className="text-center py-4">
              <Badge className="bg-status-complete/10 text-status-complete border-status-complete/30">
                All items serialized
              </Badge>
            </div>
          )}

          {/* Created list */}
          {createdAssets.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Created This Session</p>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => toast.success(`Printing ${createdAssets.length} labels...`)}>
                  <Printer className="h-3.5 w-3.5" /> Print All
                </Button>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-2 space-y-1">
                {createdAssets.map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50">
                    <span className="text-xs font-mono text-foreground">{a.internal_barcode}</span>
                    <span className="text-[10px] text-muted-foreground">{a.category_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleDone}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
